import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { path, sortWith, descend } from 'ramda'
import Avatar from '@material-ui/core/Avatar'
import Button from '@material-ui/core/Button'
import RefreshIcon from '@material-ui/icons/Refresh'
import Container from '@material-ui/core/Container'
import Grid from '@material-ui/core/Grid'
import Box from '@material-ui/core/Box'
import Typography from '@material-ui/core/Typography'
import { DateTime } from 'luxon'
import { NextSeo } from 'next-seo'
import { GraphQLClient } from 'graphql-request'
import ArticleHeader from '@components/story-header'
import ArticleHero from '@components/story-hero'
import { JsonLd } from '@components/atoms'
import RenderRichText from '@components/renderers/render-rich-text'
import defaults from '@config/defaults'
import { liveEventPostFragment } from './live-event.fragment'
import useStyles, {
  AuthorContainer, LiveEventCard, LiveEventCardActions, LiveEventCardContent,
} from './live-event.styles'

const ctflUrl = process.env.CTFL_URL
const ctflEnv = process.env.CTFL_ENV
const endpoint = ctflUrl + ctflEnv

const graphQLClient = new GraphQLClient(endpoint, {
  headers: {
    authorization: `Bearer ${process.env.CTFL_DELIVERY_TOKEN}`,
  },
})

const query = `
  query LiveEventBySlug($slug: String, $skip: Int, $limit: Int) {
    liveEventPostCollection(skip: $skip, limit: $limit, order: [sys_firstPublishedAt_DESC], where: {liveEvent: {slug: $slug}}) {
      ${liveEventPostFragment}
    }
  }
`

const sortByPublishDate = sortWith([descend(path(['sys', 'firstPublishedAt']))])

const extractPlainText = (node) => {
  if (node != null) {
    if (node.nodeType === 'text') {
      return node.value.length > 0 ? node.value : null
    }
    const nonNullText = node.content.map(nc => extractPlainText(nc)).filter(t => t != null)
    return nonNullText.length > 0 ? nonNullText.join(' ') : null
  }
  return null
}

const LiveEvent = (props) => {
  const {
    eventPage: {
      coverageStartTime,
      coverageEndTime,
      headline,
      heroImage,
      heroCaption,
      heroSource,
      metaDescription,
      liveEventRichText,
      sys,
    },
    liveUpdates: _liveUpdates,
    slug,
  } = props

  const [liveUpdates, setLiveUpdates] = useState(_liveUpdates.items)
  const styles = useStyles()

  // merge the list of fetched posts with the current list of posts
  // dedupe by id, and sort in descending order by publish date
  const mergeUpdates = (morePosts) => {
    const mergedPosts = [...liveUpdates, ...morePosts].reduce((acc, val) => {
      if (!(val.sys.id in acc.ids)) {
        acc.ids[val.sys.id] = true
        acc.posts.push(val)
      }
      return acc
    }, {posts: [], ids: {}}).posts
    setLiveUpdates(sortByPublishDate(mergedPosts))
  }

  const onClickLoadNewer = async () => {
    const response = await graphQLClient.request(query, { slug, skip: 0, limit: 10 })
    const latestPosts = path(['liveEventPostCollection', 'items'], response) || []
    mergeUpdates(latestPosts)
  }

  const onClickLoadOlder = async () => {
    const response = await graphQLClient.request(query, { slug, skip: liveUpdates.length, limit: 10 })
    const olderPosts = path(['liveEventPostCollection', 'items'], response) || []
    mergeUpdates(olderPosts)
  }

  return (
    <>
      <NextSeo
        title={headline}
        description={metaDescription}
        canonical={`${defaults.baseUrl}events/${slug}/live`}
      />
      <Container>
        <ArticleHeader cat={{ category: 'Live Event' }} headline={headline} />
      </Container>
      {heroImage && (
        <Container>
          <ArticleHero
            image={heroImage}
            heroCaption={heroCaption}
            heroSource={heroSource}
          />
        </Container>
      )
      }
      <Box maxWidth="1150px" mx="auto" mb={[6, 6, 8]}>
        <Container data-cy="article-body">
          <Grid container spacing={6} justifyContent="center">
            <Grid item xs={12} md={8}>
              <Box pr={[0, 0, 3]}>
                <RenderRichText {...liveEventRichText} />
                <Box textAlign="center">
                  <Button color="secondary" size="small" onClick={onClickLoadNewer}>
                    Load Latest <RefreshIcon />
                  </Button>
                </Box>
                {liveUpdates.map(({
                  livePostAuthor, body, sys: { firstPublishedAt },
                }) => {
                  return (
                    <LiveEventCard key={`${livePostAuthor}-${firstPublishedAt}`}>
                      <LiveEventCardContent>
                        <RenderRichText {...body} />
                      </LiveEventCardContent>
                      <LiveEventCardActions>
                        <AuthorContainer>
                          <Avatar className={styles.avatar} alt={livePostAuthor.name} src={livePostAuthor.avatar.url} />
                          <Typography variant="body1">{livePostAuthor.name}</Typography>
                        </AuthorContainer>
                        <Typography variant="body2">{DateTime.fromISO(firstPublishedAt).toRelative()}</Typography>
                      </LiveEventCardActions>
                    </LiveEventCard>
                  )
                })}
              </Box>
            </Grid>
          </Grid>
          <Box textAlign="center">
            {_liveUpdates.total > liveUpdates.length ? (
              <Button color="secondary" variant="contained" onClick={onClickLoadOlder}>Load More</Button>
            ) : null }
          </Box>
        </Container>
      </Box>
      <JsonLd {...{
        '@type': 'LiveBlogPosting',
        headline,
        description: metaDescription,
        coverageStartTime,
        coverageEndTime,
        datePublished: sys.firstPublishedAt,
        dateModified: sys.publishedAt,
        url: `${defaults.baseUrl}events/${slug}/live`,
        liveBlogUpdate: _liveUpdates && _liveUpdates.items.map(({
          livePostAuthor, livePostPublisher, body, sys: { publishedAt },
        }) => {
          const plainText = extractPlainText(body && body.json) || ''
          return ({
            '@type': 'BlogPosting',
            datePublished: publishedAt,
            headline: plainText.substr(0, 110),
            articleBody: plainText,
            author: {
              '@type': 'Person',
              name: livePostAuthor.name,
            },
            publisher: {
              '@type': 'Organization',
              name: livePostPublisher.name,
              logo: {
                '@type': 'ImageObject',
                url: livePostPublisher.logo.url,
                width: livePostPublisher.logo.width,
                height: livePostPublisher.logo.height,
              },
            },
          })
        }),
      }}
      />
    </>
  )
}

LiveEvent.propTypes = {
  coverageEndTime: PropTypes.string,
  coverageStartTime: PropTypes.string,
  headline: PropTypes.string,
  heroImage: PropTypes.string,
  heroCaption: PropTypes.string,
  heroSource: PropTypes.string,
  liveEventRichText: PropTypes.string,
  liveUpdatesCollection: PropTypes.arrayOf(PropTypes.shape({
    items: PropTypes.object,
  })),
  metaDescription: PropTypes.string,
  slug: PropTypes.string,
  sys: PropTypes.shape({
    datePublished: PropTypes.string,
    publishedAt: PropTypes.string,
  }),
}

LiveEvent.defaultProps = {}

export default LiveEvent
